import type { ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { CanGuard } from '../../client/permissions';
import { EmptyState } from '../shared/EmptyState';

export interface NoReadAccessStateProps {
  /** Permission slug the page requires. Named in the copy; the catalog is itself gated. */
  permission: string;
}

/**
 * Body shown when a Configure / Connect destination is reachable in nav
 * but the caller lacks the page's read permission. Ask an admin; do not
 * hide the row.
 *
 * Lives in the design system rather than the host app because plugin pages
 * need it too: a plugin cannot import from `web/src`, and the alternative —
 * rendering nothing where a permission is missing — makes two rows of the
 * same Configure group disagree about what a missing read looks like.
 */
export function NoReadAccessState({ permission }: NoReadAccessStateProps) {
  return (
    <EmptyState
      variant="fullHeight"
      icon={<Lock className="icon-md text-muted-foreground" />}
      title="You don't have access to this page"
      description={`Requires the ${permission} permission. Ask an organization admin to grant it.`}
    />
  );
}

export interface HubReadGuardProps {
  permission?: string;
  /** Team-scoped check; pass for Credentials team sections. */
  team?: string | null;
  children: ReactNode;
}

/**
 * Renders `children` when the section has no read slug, or when
 * {@link CanGuard} passes. Otherwise {@link NoReadAccessState}.
 */
export function HubReadGuard({ permission, team, children }: HubReadGuardProps) {
  if (!permission) return <>{children}</>;
  return (
    <CanGuard
      permission={permission}
      team={team}
      fallback={<NoReadAccessState permission={permission} />}
    >
      {children}
    </CanGuard>
  );
}
